/**
 * companionVoice.js — one voice table for Compass (the MWQ guide) and the
 * 8 selectable companions. Varied, age-aware, never repeated back-to-back.
 * Read-only over lib/companion.js; no new storage besides a rotation index.
 */
import { getCompanion } from "@/lib/companion";

export const COMPASS = { id: "compass", name: "Compass", emoji: "🧭" };

// Lightweight personality guidance per companion (quirk + signature).
export const PERSONALITY = {
  globy:  { style: "world-curious", signature: "Somewhere on the globe, someone just learned that too.", quirk: "collects flags" },
  foxy:   { style: "quick, plan-loving", signature: "Plan made. Plan done.", quirk: "counts steps ahead" },
  panda:  { style: "calm, thoughtful", signature: "No rush. You got there.", quirk: "pauses before answering" },
  koala:  { style: "chill, patient", signature: "Easy does it — and it did.", quirk: "yawns when proud" },
  owl:    { style: "bookish, question-loving", signature: "Now that is a good question.", quirk: "loves footnotes" },
  cat:    { style: "playful, puzzle-loving", signature: "Puzzle: solved. Next one?", quirk: "bats at bright things" },
  dragon: { style: "brave, imaginative", signature: "Bold move, explorer.", quirk: "tiny smoke ring when happy" },
  star:   { style: "bright, celebratory", signature: "Shine on!", quirk: "twinkles at good answers" },
  compass:{ style: "steady guide", signature: "Onwards.", quirk: "always points to the next step" },
};

const LINES = {
  correct: {
    seedlings: ["Yes! That's it!", "You found it!", "Look at you go!", "Spot on!", "Brilliant find!"],
    explorers: ["Correct — nicely reasoned.", "That's the one.", "Good thinking.", "Exactly right.", "You've got this pattern."],
    pioneers:  ["Correct.", "Right — clean reasoning.", "That checks out.", "Good. Next.", "Solid answer."],
  },
  tricky: {
    seedlings: ["Nearly! Let's look again.", "Good try — here's the answer.", "Tricky one! Now we know."],
    explorers: ["Not this time — here's why.", "Close. The key idea is below.", "Tricky question. Worth a second look."],
    pioneers:  ["Not quite — see the explanation.", "Here's the reasoning.", "A common slip. Now you've seen it."],
  },
  complete: {
    seedlings: ["All done! High five!", "You finished the whole thing!", "What an explorer!"],
    explorers: ["Quest complete.", "Finished — that's another one for the journey.", "Done. Your map just grew."],
    pioneers:  ["Complete.", "Finished — progress recorded.", "Done. That skill just got stronger."],
  },
  badge: {
    seedlings: ["A new badge! Shiny!", "Badge time! You earned it!"],
    explorers: ["New badge for your collection.", "Badge earned — check your collection."],
    pioneers:  ["Badge earned.", "New achievement added."],
  },
  stamp: {
    seedlings: ["Stamp! A new place in your passport!", "Where to next?"],
    explorers: ["New passport stamp.", "Another country discovered."],
    pioneers:  ["Passport updated.", "New country recorded."],
  },
  levelup: {
    seedlings: ["Wow — you levelled up!", "A whole new level!"],
    explorers: ["Level up! Your journey continues.", "New level reached."],
    pioneers:  ["New level reached.", "Level up — steady progress."],
  },
  discovery: {
    seedlings: ["Look what we found!", "Ooh, a discovery!"],
    explorers: ["Discovery made.", "That's new on your map."],
    pioneers:  ["New discovery.", "Added to your collection."],
  },
  story: {
    seedlings: ["What happens next? Let's find out!", "The story goes on…"],
    explorers: ["The story continues.", "One more chapter awaits."],
    pioneers:  ["Next chapter available.", "The story continues."],
  },
  return: {
    seedlings: ["You're back! Let's explore.", "Hello again, explorer!"],
    explorers: ["Welcome back. Ready for the next quest?", "Good to see you again."],
    pioneers:  ["Welcome back.", "Ready when you are."],
  },
  challenge: {
    seedlings: ["Let's try a bigger one!", "Ready for a challenge?"],
    explorers: ["Up for a challenge?", "A tougher one — you're ready."],
    pioneers:  ["Choose a challenge.", "Ready for something harder?"],
  },
  surprise: {
    seedlings: ["Oh! A surprise!", "Look — something special!"],
    explorers: ["Unexpected discovery!", "Oh — didn't see that coming."],
    pioneers:  ["Unexpected find.", "A small bonus."],
  },
  xp: {
    seedlings: ["Points for you!", "More stars!"],
    explorers: ["XP earned.", "Progress added."],
    pioneers:  ["XP added.", "Progress recorded."],
  },
};

const ROT_KEY = "mwq_voice_rot_v1";
function nextIndex(bucket, len) {
  try {
    const rot = JSON.parse(sessionStorage.getItem(ROT_KEY) || "{}");
    const i = ((rot[bucket] ?? -1) + 1) % len;
    rot[bucket] = i;
    sessionStorage.setItem(ROT_KEY, JSON.stringify(rot));
    return i;
  } catch { return Math.floor(Math.random() * len); }
}

/** voiceLine("correct", { tier }) → { text, speaker } — varied, tier-aware, companion-flavoured. */
export function voiceLine(moment, { tier = "explorers", speaker } = {}) {
  const bank = LINES[moment] || LINES.correct;
  const arr = bank[tier] || bank.explorers;
  const i = nextIndex(`${moment}:${tier}`, arr.length);
  const who = speaker || getCompanion() || COMPASS;
  const p = PERSONALITY[who.id] || PERSONALITY.compass;
  const sig = (moment === "complete" || moment === "levelup" || moment === "badge") && i % 3 === 2 && tier !== "seedlings" ? ` ${p.signature}` : "";
  return { text: `${arr[i]}${sig}`, speaker: { id: who.id, name: who.name, emoji: who.emoji, color: who.color || "bg-slate-100" } };
}
